import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When this value changes, a caught error is cleared and children re-render.
   * Pass the current route (e.g. pathname) so navigating away from an errored
   * page recovers automatically — without it the boundary stays on the
   * fallback until a full page reload.
   */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div className='flex flex-col items-center justify-center h-full gap-3 p-8 text-center'>
            <p className='text-sm font-medium text-destructive'>
              Something went wrong
            </p>
            <p className='text-xs text-muted-foreground font-mono break-all max-w-md'>
              {this.state.error.message}
            </p>
            <button
              type='button'
              onClick={() => this.setState({ error: null })}
              className='text-xs underline text-muted-foreground hover:text-foreground'
            >
              Try again
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
